import { useMemo, useState } from "react";
import { Button } from "@/components/Button";
import { useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useExistingGroupNames } from "@/lib/hooks/usePeople";
import { call, CALLABLES } from "@/services/callables";
import { SELECTABLE_AGE_BANDS, summarise } from "../review";
import type {
  AgeBand,
  CommitImportResult,
  ImportSkip,
  ParsedUpload,
  ReviewedRow,
} from "@/types";

/**
 * Step three: every person, before anything is created.
 *
 * This step cannot be skipped. Not because a flag says so, but because there is
 * no path from a parsed file to `commitImport` that does not pass through here:
 * the backend takes reviewed rows and nothing else.
 *
 * **Commit is disabled while any blocking problem remains.** A warning — two
 * people with the same name in one class — does not block, because that is
 * ordinary in a school of any size and refusing it would be refusing reality.
 */
export function ReviewStep({
  upload,
  rows,
  unreadable,
  onChange,
  onBack,
  onCommitted,
}: {
  upload: ParsedUpload;
  rows: ReviewedRow[];
  /** Rows the mapper could not read at all. They never reach the grid. */
  unreadable: readonly ImportSkip[];
  onChange: (rows: ReviewedRow[]) => void;
  onBack: () => void;
  onCommitted: (result: CommitImportResult) => void;
}) {
  const t = useT();
  const { showError } = useToast();
  const existingGroups = useExistingGroupNames();

  const [excluded, setExcluded] = useState<ReadonlySet<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const summary = useMemo(
    () => summarise(rows, excluded, existingGroups),
    [rows, excluded, existingGroups]
  );

  const update = (rowNumber: number, patch: Partial<ReviewedRow>) => {
    onChange(rows.map((row) => (row.rowNumber === rowNumber ? { ...row, ...patch } : row)));
  };

  const toggleExcluded = (rowNumber: number) => {
    const next = new Set(excluded);
    if (next.has(rowNumber)) next.delete(rowNumber);
    else next.add(rowNumber);
    setExcluded(next);
  };

  const commit = async () => {
    setBusy(true);
    try {
      const result = await call<
        { rows: ReviewedRow[]; fingerprint: string },
        CommitImportResult
      >(CALLABLES.commitImport, {
        rows: rows.filter((row) => !excluded.has(row.rowNumber)),
        fingerprint: upload.fingerprint,
      });
      onCommitted(result);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h2 className="section-title">{t("import.review.title")}</h2>
      <p className="mt-2 max-w-2xl text-sm text-ink-soft">{t("import.review.intro")}</p>

      <p className="mt-4 text-sm">
        <span className={summary.needAttention > 0 ? "text-danger" : "text-positive"}>
          {t("import.review.summary", {
            ready: summary.ready,
            attention: summary.needAttention,
          })}
        </span>
        {summary.excluded > 0 ? (
          <span className="ml-3 text-ink-muted">
            {t("import.review.excludedCount", { count: summary.excluded })}
          </span>
        ) : null}
      </p>

      {unreadable.length > 0 ? (
        /*
          Rows that never made it into the grid. An owner who sent thirty rows
          and is shown twenty-nine has to be told which one is missing and why,
          or the count is simply wrong and nobody can say where.
        */
        <div className="mt-4 rounded-card border border-warning bg-warning-soft px-4 py-3">
          <p className="text-sm text-ink">
            {t("import.review.unreadable", { count: unreadable.length })}
          </p>
          <ul className="mt-2 flex flex-col gap-1 text-sm text-ink-soft">
            {unreadable.map((skip) => (
              <li key={skip.row}>
                <span className="text-ink-faint">{skip.row}</span> {t(skip.reasonKey)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="card mt-6 overflow-x-auto">
        <table className="w-full border-collapse text-[14px]">
          <caption className="sr-only">{t("import.review.title")}</caption>
          <thead>
            <tr className="border-b border-line bg-surface-muted text-left text-ink-muted">
              <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("import.review.column.row")}</th>
              <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("import.review.column.name")}</th>
              <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("import.review.column.role")}</th>
              <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("import.review.column.group")}</th>
              <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("import.review.column.ageBand")}</th>
              <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("import.review.column.problems")}</th>
              <th scope="col" className="h-9 px-3 text-[13px] font-medium">
                <span className="sr-only">{t("import.review.exclude")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isExcluded = excluded.has(row.rowNumber);
              const problems = summary.problems.get(row.rowNumber) ?? [];
              const blocking = problems.some((problem) => problem.severity === "blocking");

              return (
                <tr
                  key={row.rowNumber}
                  className={`border-b border-line last:border-0 ${
                    isExcluded ? "opacity-40" : blocking ? "bg-danger-soft" : ""
                  }`}
                >
                  <td className="px-3 py-2 text-ink-faint">{row.rowNumber}</td>
                  <td className="px-3 py-2">
                    <input
                      className="w-full rounded-card border border-line px-2 py-1"
                      value={row.displayName}
                      aria-label={t("import.review.column.name")}
                      disabled={isExcluded}
                      onChange={(event) =>
                        update(row.rowNumber, { displayName: event.target.value })
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="rounded-card border border-line px-2 py-1"
                      value={row.role}
                      aria-label={t("import.review.column.role")}
                      disabled={isExcluded}
                      onChange={(event) =>
                        update(row.rowNumber, {
                          role: event.target.value as "teacher" | "student",
                        })
                      }
                    >
                      <option value="student">{t("role.student")}</option>
                      <option value="teacher">{t("role.teacher")}</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      className="w-full rounded-card border border-line px-2 py-1"
                      value={row.groupName ?? ""}
                      aria-label={t("import.review.column.group")}
                      disabled={isExcluded}
                      onChange={(event) =>
                        update(row.rowNumber, {
                          groupName: event.target.value.trim() === "" ? undefined : event.target.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="rounded-card border border-line px-2 py-1"
                      value={row.ageBand ?? ""}
                      aria-label={t("import.review.column.ageBand")}
                      disabled={isExcluded}
                      onChange={(event) =>
                        update(row.rowNumber, {
                          ageBand: event.target.value === "" ? undefined : (event.target.value as AgeBand),
                        })
                      }
                    >
                      <option value="">{t("import.ageBand.none")}</option>
                      {SELECTABLE_AGE_BANDS.map((band) => (
                        <option key={band} value={band}>
                          {t(`import.ageBand.${band}`)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <ul className="flex flex-col gap-1">
                      {problems.map((problem) => (
                        <li
                          key={problem.key}
                          className={
                            problem.severity === "blocking"
                              ? "text-danger"
                              : "text-warning"
                          }
                        >
                          {t(problem.key, problem.params)}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      className="text-xs text-ink-muted underline underline-offset-2 hover:text-ink"
                      onClick={() => toggleExcluded(row.rowNumber)}
                    >
                      {isExcluded ? t("import.review.include") : t("import.review.exclude")}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!summary.canCommit && summary.needAttention > 0 ? (
        <p className="mt-4 text-sm text-danger">{t("import.review.blocked")}</p>
      ) : null}

      <div className="mt-6 flex gap-2">
        <Button variant="ghost" onClick={onBack}>
          {t("import.review.back")}
        </Button>
        <Button busy={busy} disabled={!summary.canCommit} onClick={commit}>
          {t("import.review.commit", { count: summary.ready })}
        </Button>
      </div>
    </div>
  );
}
